import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from './icons/AppIcon';

// Endpoints públicos (AwesomeAPI)
// Atual: https://economia.awesomeapi.com.br/json/last/USD-BRL
// Histórico: https://economia.awesomeapi.com.br/json/daily/USD-BRL/7

const numberBRL = (v) => {
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(Number(v));
  } catch (_) {
    const n = Number(v);
    return `R$ ${n.toFixed(2)}`;
  }
};

const Sparkline = ({ points = [], color = '#9ecbff' }) => {
  if (!points.length) return null;
  const width = 160;
  const height = 48;
  const pad = 6;
  const xs = points.map((_, i) => i);
  const ys = points;
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const scaleX = (x) => pad + (x / Math.max(1, xs.length - 1)) * (width - pad * 2);
  const scaleY = (y) => {
    if (max === min) return height / 2; // linha reta se sem variação
    // Inverter y para SVG (0 em cima)
    return pad + (1 - (y - min) / (max - min)) * (height - pad * 2);
  };
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(x)},${scaleY(ys[i])}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="fx-sparkline" aria-hidden>
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="rgba(49,117,177,0.5)" />
        </filter>
      </defs>
      <path d={d} fill="none" stroke={color} strokeWidth="2" filter="url(#shadow)" />
    </svg>
  );
};

const FxCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [price, setPrice] = useState(null);
  const [pct, setPct] = useState(null);
  const [history, setHistory] = useState([]);

  const trend = useMemo(() => {
    if (pct == null) return 'neutral';
    const n = Number(pct);
    if (n > 0) return 'up';
    if (n < 0) return 'down';
    return 'neutral';
  }, [pct]);

  const trendColor = trend === 'up' ? '#53d89a' : trend === 'down' ? '#ff8585' : '#a8b3c9';
  const sparkColor = trend === 'down' ? '#ffb0b0' : '#9ecbff';

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Preço atual e variação diária
      const res = await fetch('https://economia.awesomeapi.com.br/json/last/USD-BRL');
      if (!res.ok) throw new Error(`FX error: ${res.status}`);
      const json = await res.json();
      const node = json?.USDBRL;
      const bid = Number(node?.bid);
      const pctChange = Number(node?.pctChange);
      setPrice(Number.isFinite(bid) ? bid : null);
      setPct(Number.isFinite(pctChange) ? pctChange : null);

      // Histórico para sparkline (7 pontos)
      const resHist = await fetch('https://economia.awesomeapi.com.br/json/daily/USD-BRL/7');
      if (!resHist.ok) throw new Error(`FX history error: ${resHist.status}`);
      const list = await resHist.json();
      // 'list' é array; usar bid como série e ordenar por timestamp asc
      const ordered = (Array.isArray(list) ? list : [])
        .map((it) => ({ t: Number(it?.timestamp) || 0, v: Number(it?.bid) }))
        .filter((it) => Number.isFinite(it.v))
        .sort((a, b) => a.t - b.t)
        .map((it) => it.v);
      setHistory(ordered);
    } catch (e) {
      setError(e.message || 'Falha ao carregar cotação');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      await fetchData();
    })();
    const id = setInterval(() => {
      if (mounted) fetchData();
    }, 5 * 60 * 1000); // 5 minutos
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <div className="welcome-inner-card fx-card">
      <div className="fx-top">
        <div className="fx-title">USD/BRL</div>
        {!loading && pct != null && (
          <div className={`fx-variation ${trend}`} style={{ color: trendColor }}>
            <AppIcon name={trend === 'up' ? 'trending_up' : trend === 'down' ? 'trending_down' : 'trending_flat'} style={{ fontSize: 16, verticalAlign: 'middle' }} />
            <span style={{ marginLeft: 6 }}>{pct > 0 ? '+' : ''}{Number(pct).toFixed(2)}%</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="fx-loading">Carregando cotação...</div>
      )}
      {error && !loading && (
        <div className="fx-error">{error}</div>
      )}

      {!loading && !error && (
        <div className="fx-main">
          <div className="fx-left">
            <div className="fx-price">{price != null ? numberBRL(price) : '—'}</div>
            <div className="fx-sub">Cotação em tempo quase real</div>
          </div>
          <div className="fx-right">
            <Sparkline points={history} color={sparkColor} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FxCard;
